<template>
  <a-drawer width="45%" :label-col="4" :wrapper-col="14" :visible="open" @close="onClose" :maskClosable="false">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
      <a-tag v-if="form.status === 0" color="orange" style="margin-left: 8px;">待审核</a-tag>
      <a-tag v-else-if="form.status === 1" color="green" style="margin-left: 8px;">已通过</a-tag>
      <a-tag v-else-if="form.status === -1" color="red" style="margin-left: 8px;">已驳回</a-tag>
    </a-divider>

    <a-spin :spinning="loading">
      <!-- 医师基本信息 -->
      <a-card title="医师基本信息" size="small" :bordered="false" class="info-card">
        <a-descriptions :column="2" size="small" bordered>
          <a-descriptions-item label="医院名称" :span="2">
            {{ form.hospitalName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="科室名称" :span="2">
            {{ form.departName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="姓名">
            {{ form.name || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="性别">
            <dict-tag :options="dict.type['sys_user_sex']" :value="form.sex"/>
          </a-descriptions-item>
          <a-descriptions-item label="医师形象照片" :span="2">
            <img 
              v-viewer
              v-if="form.logo" 
              :src="processImageUrl(form.logo)" 
              style="height: 80px; width: 80px; cursor: pointer; border-radius: 4px;"
            />
            <span v-else style="color: #999;">暂无</span>
          </a-descriptions-item>
          <a-descriptions-item label="行医资质证明" :span="2">
            <img 
              v-viewer
              v-if="form.certify" 
              :src="processImageUrl(form.certify)" 
              style="height: 80px; width: 80px; cursor: pointer; border-radius: 4px;"
            />
            <span v-else style="color: #999;">暂无</span>
          </a-descriptions-item>
          <a-descriptions-item label="工龄">
            {{ form.workYear ? form.workYear + '年' : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="职称">
            <dict-tag :options="dict.type['doctor_professional']" :value="form.professional"/>
          </a-descriptions-item>
          <a-descriptions-item label="擅长领域" :span="2">
            <div style="max-height: 60px; overflow-y: auto;">{{ form.strongPoint || '-' }}</div>
          </a-descriptions-item>
          <a-descriptions-item label="获得荣誉" :span="2">
            <div style="max-height: 60px; overflow-y: auto;">{{ form.honor || '-' }}</div>
          </a-descriptions-item>
          <a-descriptions-item label="荣誉证书" :span="2">
            <a v-if="form.certificate" @click="viewCertificates">
              <a-icon type="picture" /> 查看荣誉证书
            </a>
            <span v-else style="color: #999;">暂无</span>
          </a-descriptions-item>
          <a-descriptions-item label="个人简介" :span="2">
            <div style="max-height: 100px; overflow-y: auto;">{{ form.content || '-' }}</div>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <!-- 审核操作 -->
      <a-card title="审核操作" size="small" :bordered="false" class="info-card" style="margin-top: 16px;" v-if="isInfo == 1">
        <a-form-model ref="form" :model="form" :rules="rules" layout="vertical">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-model-item label="审核状态" prop="status">
                <a-radio-group v-model="form.status" @change="handleStatusChange">
                  <a-radio :value="1">
                    <a-tag color="green">通过</a-tag>
                  </a-radio>
                  <a-radio :value="-1">
                    <a-tag color="red">驳回</a-tag>
                  </a-radio>
                </a-radio-group>
              </a-form-model-item>
            </a-col>
            <a-col :span="12" v-if="form.status === -1">
              <a-form-model-item label="驳回原因" prop="rejectReason">
                <a-textarea 
                  v-model="form.rejectReason" 
                  placeholder="请输入驳回原因，必填" 
                  :rows="2"
                  :maxlength="200"
                  showCount
                />
              </a-form-model-item>
            </a-col>
          </a-row>
        </a-form-model>
      </a-card>

      <!-- 驳回信息（详情模式显示） -->
      <a-card title="审核结果" size="small" :bordered="false" class="info-card" style="margin-top: 16px;" v-if="isInfo == 2 && form.status !== 0">
        <a-descriptions :column="1" size="small" bordered>
          <a-descriptions-item label="审核状态">
            <a-tag v-if="form.status === 1" color="green">已通过</a-tag>
            <a-tag v-else-if="form.status === -1" color="red">已驳回</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="驳回原因" v-if="form.status === -1">
            <span style="color: #f5222d;">{{ form.rejectReason || '-' }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>
    </a-spin>

    <!-- 荣誉证书预览modal -->
    <a-modal
      v-model="certificatePreviewVisible"
      title="荣誉证书预览"
      :footer="null"
      :width="800"
      centered
    >
      <div style="text-align: center;">
        <img
          :src="certificateImages[currentPreviewIndex]"
          style="max-width: 100%; max-height: 600px;"
          alt="荣誉证书"
        />
        <div v-if="certificateImages.length > 1" style="margin-top: 16px;">
          <a-button @click="prevImage" :disabled="currentPreviewIndex === 0">
            上一张
          </a-button>
          <span style="margin: 0 16px;">
            {{ currentPreviewIndex + 1 }} / {{ certificateImages.length }}
          </span>
          <a-button @click="nextImage" :disabled="currentPreviewIndex === certificateImages.length - 1">
            下一张
          </a-button>
        </div>
      </div>
    </a-modal>

    <div class="bottom-control">
      <a-space>
        <template v-if="isInfo == 1">
          <a-button type="primary" :loading="submitLoading" @click="submitForm" :disabled="form.status === 0">
            <a-icon type="check" />提交审核
          </a-button>
          <a-button @click="cancel">
            取消
          </a-button>
        </template>
        <a-button v-else @click="cancel">
          关闭
        </a-button>
      </a-space>
    </div>
  </a-drawer>
</template>

<script>
import { getAuth, addAuth, updateAuth, checkDoctor } from '@/api/doctor/auth'
import Editor from '@/components/Editor'
import { processImageUrl } from '@/utils/util'

export default {
  name: 'CreateForm',
  props: {
  },
  components: {
    Editor
  },
  dicts: ['sys_user_sex', 'doctor_professional'],
  data () {
    return {
      submitLoading: false,
      loading: false,
      formTitle: '',
      isInfo: 1,
      // 表单参数
      form: {
        id: null,
        userId: null,
        hospitalId: null,
        hospitalName: null,
        departName: null,
        departId: null,
        name: null,
        sex: null,
        certify: null,
        logo: null,
        workYear: null,
        professional: null,
        professionalName: null,
        honor: null,
        honorName: null,
        strongPoint: null,
        content: null,
        certificate: null,
        status: 0,
        rejectReason: null,
        score: null,
        createTime: null,
        remark: null
      },
      // 1增加,2修改
      formType: 1,
      open: false,
      certificatePreviewVisible: false,
      certificateImages: [],
      currentPreviewIndex: 0,
      rules: {
        status: [{ required: true, message: '请选择审核状态', trigger: 'change' }],
        rejectReason: [{ required: true, message: '请输入驳回原因', trigger: 'blur' }]
      }
    }
  },
  filters: {
  },
  created () {
  },
  computed: {
  },
  watch: {
  },
  mounted () {
  },
  methods: {
    processImageUrl,
    onClose () {
      this.open = false
    },
    // 取消按钮
    cancel () {
      this.open = false
      this.reset()
    },
    // 表单重置
    reset () {
      this.isInfo = 1
      this.formType = 1
      this.form = {
        id: null,
        userId: null,
        hospitalId: null,
        hospitalName: null,
        departName: null,
        departId: null,
        name: null,
        sex: null,
        certify: null,
        logo: null,
        workYear: null,
        professional: null,
        honor: null,
        honorName: null,
        strongPoint: null,
        content: null,
        certificate: null,
        status: 0,
        rejectReason: null,
        score: null,
        createTime: null,
        remark: null
      }
      this.certificateImages = []
      this.currentPreviewIndex = 0
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetFields()
        }
      })
    },
    /** 新增按钮操作 */
    handleAdd (row) {
      this.reset()
      this.formType = 1
      this.open = true
      this.formTitle = '添加'
    },
    /** 修改按钮操作 */
    handleUpdate (row, ids) {
      this.reset()
      this.formType = 2
      const id = row ? row.id : ids
      this.isInfo = 1
      this.loading = true
      getAuth({ 'id': id }).then(response => {
        this.form = response.data
        this.form.status = 0
        this.open = true
        this.formTitle = '医师认证审核'
      }).finally(() => {
        this.loading = false
      })
    },
    handleInfo (row) {
      this.reset()
      this.formType = 2
      const id = row ? row.id : ids
      this.isInfo = 2
      this.loading = true
      getAuth({ 'id': id }).then(response => {
        this.form = response.data
        this.open = true
        this.formTitle = '医师认证详情'
      }).finally(() => {
        this.loading = false
      })
    },
    handleStatusChange () {
      if (this.form.status !== -1) {
        this.form.rejectReason = null
      }
    },
    /** 提交按钮 */
    submitForm () {
      if (this.form.status === 0) {
        this.$message.warning('请选择审核状态')
        return
      }
      if (this.form.status === -1 && !this.form.rejectReason) {
        this.$message.warning('请输入驳回原因')
        return
      }
      this.$confirm({
        title: '确认审核',
        content: this.form.status === 1 ? '确认通过该医师的认证申请？' : '确认驳回该医师的认证申请？',
        onOk: () => {
          this.submitLoading = true
          checkDoctor(this.form).then(response => {
            this.$message.success('审核成功')
            this.open = false
            this.$emit('ok')
          }).finally(() => {
            this.submitLoading = false
          })
        }
      })
    },
    // 查看荣誉证书
    viewCertificates () {
      if (this.form.certificate) {
        this.certificateImages = this.form.certificate.split(',').map(url => processImageUrl(url))
        this.currentPreviewIndex = 0
        this.certificatePreviewVisible = true
      } else {
        this.$message.warning('暂无荣誉证书')
      }
    },
    // 上一张图片
    prevImage () {
      if (this.currentPreviewIndex > 0) {
        this.currentPreviewIndex--
      }
    },
    // 下一张图片
    nextImage () {
      if (this.currentPreviewIndex < this.certificateImages.length - 1) {
        this.currentPreviewIndex++
      }
    }
  }
}
</script>

<style scoped>
.info-card {
  background: #fafafa;
}
.info-card >>> .ant-card-head {
  border-bottom: 1px solid #e8e8e8;
  padding: 0 12px;
  min-height: 40px;
}
.info-card >>> .ant-card-head-title {
  padding: 8px 0;
  font-size: 14px;
}
.info-card >>> .ant-card-body {
  padding: 12px;
}
.info-card >>> .ant-descriptions-item-label {
  min-width: 100px;
  white-space: nowrap;
}
.info-card >>> .ant-descriptions-item-content {
  min-width: 150px;
}
.bottom-control {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  padding: 12px 24px;
  text-align: right;
  z-index: 10;
}
</style>
